import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layers, Plus, Edit, Trash2, AlertCircle, X, Check, Users, Package, FileText, LogOut, BarChart3, Building2, Ship } from 'lucide-react';
import tipoEquipoService from '../services/tipoEquipoService';
import { authService } from '../services/authService';

function TiposEquipo() {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedTipo, setSelectedTipo] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    tarifa_mensual: ''
  });
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadTipos();
  }, []);

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const loadTipos = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await tipoEquipoService.getTiposEquipo();
      setTipos(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (mode, tipo = null) => {
    setModalMode(mode);
    setSelectedTipo(tipo);
    
    if (mode === 'create') {
      setFormData({
        nombre: '',
        descripcion: '',
        tarifa_mensual: ''
      });
    } else if (mode === 'edit' && tipo) {
      setFormData({
        nombre: tipo.nombre,
        descripcion: tipo.descripcion || '',
        tarifa_mensual: tipo.tarifa_mensual
      });
    }
    
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTipo(null);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const submitData = {
        ...formData,
        tarifa_mensual: parseFloat(formData.tarifa_mensual)
      };

      if (modalMode === 'create') {
        await tipoEquipoService.createTipoEquipo(submitData);
        setSuccess('Tipo de equipo creado exitosamente');
      } else if (modalMode === 'edit') {
        await tipoEquipoService.updateTipoEquipo(selectedTipo.id, submitData);
        setSuccess('Tipo de equipo actualizado exitosamente');
      }
      
      await loadTipos();
      setTimeout(() => {
        handleCloseModal();
      }, 1500);
    } catch (err) {
      setError(err);
    }
  };

  const handleDelete = async (tipoId) => {
    if (!window.confirm('¿Estás seguro de desactivar este tipo de equipo?')) {
      return;
    }

    try {
      setError('');
      await tipoEquipoService.deleteTipoEquipo(tipoId);
      setSuccess('Tipo de equipo desactivado exitosamente');
      await loadTipos();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div className="w-64 bg-gradient-to-b from-slate-900 to-blue-900 text-white">
        <div className="p-6 border-b border-blue-800">
          <div className="flex items-center gap-3">
            <div className="bg-blue-500 p-2 rounded-lg">
              <Ship size={24} />
            </div>
            <div>
              <h1 className="font-bold text-lg">MarineMonitor</h1>
              <p className="text-xs text-blue-200">Multi-Plataforma</p>
            </div>
          </div>
        </div>

        <nav className="p-4 space-y-2">
          <Link
            to="/dashboard"
            className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition"
          >
            <BarChart3 size={20} />
            <span>Dashboard</span>
          </Link>

          <Link
            to="/equipos"
            className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition"
          >
            <Package size={20} />
            <span>Equipos</span>
          </Link>

          <Link
            to="/tipos-equipo"
            className="flex items-center gap-3 px-4 py-3 bg-blue-800 text-white rounded-lg"
          >
            <Layers size={20} />
            <span>Tipos de Equipo</span>
          </Link>

          {user?.rol === 'admin' && (
            <Link
              to="/clientes"
              className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition"
            >
              <Building2 size={20} />
              <span>Clientes</span>
            </Link>
          )}

          {user?.rol === 'admin' && (
            <Link
              to="/users"
              className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition"
            >
              <Users size={20} />
              <span>Usuarios</span>
            </Link>
          )}
            <Link
              to="/solicitudes"
              className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition"
            >
              <FileText size={20} />
              <span>Solicitudes</span>
            </Link>
          <Link
            to="/informes"
            className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-blue-800 hover:text-white rounded-lg transition"
          >
            <FileText size={20} />
            <span>Informes</span>
          </Link>
        </nav>

        <div className="absolute bottom-0 w-64 p-4 border-t border-blue-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">{user?.nombre}</p>
              <p className="text-xs text-blue-200">{user?.rol}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 hover:bg-blue-800 rounded-lg transition"
              title="Cerrar Sesión"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="bg-white shadow-sm p-6 flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-800">Tipos de Equipo</h1>
          {user?.rol === 'admin' && (
            <button
              onClick={() => handleOpenModal('create')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <Plus size={20} />
              Crear Tipo
            </button>
          )}
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <Check className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-green-700 text-sm">{success}</p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-xl text-gray-500">Cargando tipos de equipo...</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tipos.length === 0 ? (
                <div className="col-span-full text-center py-12 text-gray-500">
                  No hay tipos de equipo registrados
                </div>
              ) : (
                tipos.map((tipo) => (
                  <div key={tipo.id} className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-blue-100 p-3 rounded-lg">
                          <Layers className="text-blue-600" size={24} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg text-gray-800">{tipo.nombre}</h3>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            tipo.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {tipo.activo ? 'Activo' : 'Inactivo'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <p className="text-gray-600 text-sm mb-4 min-h-[60px]">
                      {tipo.descripcion || 'Sin descripción'}
                    </p>

                    <div className="border-t pt-4 mb-4">
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold text-blue-600">
                          ${parseFloat(tipo.tarifa_mensual).toFixed(2)}
                        </span>
                        <span className="text-gray-500 text-sm">/mes</span>
                      </div>
                    </div>

                    {user?.rol === 'admin' && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleOpenModal('edit', tipo)}
                          className="flex-1 px-3 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg flex items-center justify-center gap-2 transition"
                        >
                          <Edit size={16} />
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(tipo.id)}
                          className="flex-1 px-3 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg flex items-center justify-center gap-2 transition"
                        >
                          <Trash2 size={16} />
                          Desactivar
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {modalMode === 'create' ? 'Crear Tipo de Equipo' : 'Editar Tipo de Equipo'}
              </h2>
              <button onClick={handleCloseModal} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                {error}
              </div>
            )}

            {success && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
                {success}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción
                </label>
                <textarea
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tarifa Mensual ($) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="tarifa_mensual"
                  value={formData.tarifa_mensual}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                >
                  {modalMode === 'create' ? 'Crear' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TiposEquipo;